import React, { useRef, useState } from "react";
import axios from "axios";
import { Upload, Send, Loader2, FileSpreadsheet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK = 200;
const DEFAULT_TPL = "Sayın {yetkili}, {firma} firmanızı dugunsenin.com'a ücretsiz kaydedin, düğün hazırlığındaki binlerce çifte ulaşın: dugunsenin.com/firma-kayit";
const fill = (t, r) => t.replace(/\{firma\}/g, r?.firma || "firmanız").replace(/\{yetkili\}/g, r?.yetkili || "Yetkili").replace(/\s+/g, " ").trim();
const parts = (n) => (n <= 155 ? 1 : Math.ceil(n / 150));
const fmt = (n) => (n || 0).toLocaleString("tr-TR");

const Count = ({ label, value, tone, testid }) => (
  <div className={`rounded-xl px-4 py-3 ${tone}`} data-testid={testid}>
    <p className="text-xs opacity-80">{label}</p>
    <p className="text-xl font-bold">{fmt(value)}</p>
  </div>
);

export const BulkFirmSms = ({ headers }) => {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [data, setData] = useState(null);
  const [tpl, setTpl] = useState(DEFAULT_TPL);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [progress, setProgress] = useState(null);

  const ready = (data?.rows || []).filter((r) => r.state === "ok");
  const sample = fill(tpl, ready[0] || { firma: "Örnek Düğün Salonu", yetkili: "Ahmet Yılmaz" });

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await axios.post(`${API}/admin/bulk-sms/preview`, fd, { headers });
      setData(r.data);
    } catch (e) {
      toast({ title: "Dosya okunamadı", description: e?.response?.data?.detail || "Tekrar deneyin." });
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const send = async () => {
    setConfirm(false);
    const tot = { sent: 0, failed: 0, skipped: 0 };
    setProgress({ done: 0, total: ready.length });
    try {
      for (let i = 0; i < ready.length; i += CHUNK) {
        const rows = ready.slice(i, i + CHUNK).map(({ phone, firma, yetkili }) => ({ phone, firma, yetkili }));
        const { data: d } = await axios.post(`${API}/admin/bulk-sms/send`, { template: tpl, rows }, { headers });
        tot.sent += d.sent; tot.failed += d.failed; tot.skipped += d.skipped;
        setProgress({ done: Math.min(i + CHUNK, ready.length), total: ready.length });
      }
      toast({ title: "Gönderim tamamlandı", description: `${fmt(tot.sent)} firmaya SMS gönderildi${tot.failed ? `, ${fmt(tot.failed)} başarısız` : ""}.` });
      setData((d) => ({ ...d, rows: d.rows.map((r) => (r.state === "ok" ? { ...r, state: "sent" } : r)), counts: { ...d.counts, ok: 0, sent: d.counts.sent + tot.sent } }));
    } catch (e) {
      toast({ title: "Gönderim durdu", description: `${fmt(tot.sent)} firmaya gönderildi. ${e?.response?.data?.detail || ""}` });
    } finally { setProgress(null); }
  };

  return (
    <div className="space-y-5" data-testid="bulk-firm-sms">
      <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5">
        <p className="text-sm font-medium text-[#2A2224]">1. Firma listesini yükleyin</p>
        <p className="text-xs text-[#7C6E70] mt-1">Excel'de "Firma Adı", "Yetkili", "Cep Telefonu" ve "İl" sütunları olmalı. Daha önce SMS gönderilmiş numaralar otomatik atlanır.</p>
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="bulk-sms-file" />
        <button onClick={() => fileRef.current?.click()} disabled={busy || !!progress} data-testid="bulk-sms-upload-btn"
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[#9E2A3B] text-[#9E2A3B] text-sm font-medium hover:bg-[#FBEEF0] transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Excel Dosyası Seç
        </button>
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            <Count label="Gönderilecek" value={data.counts.ok} tone="bg-[#E6F4EA] text-[#2f7d4f]" testid="bulk-count-ok" />
            <Count label="Daha önce gönderilmiş" value={data.counts.sent} tone="bg-[#FBF5F0] text-[#7C6E70]" testid="bulk-count-sent" />
            <Count label="Geçersiz numara" value={data.counts.invalid} tone="bg-[#FBEAEA] text-[#C0392B]" testid="bulk-count-invalid" />
            <Count label="Listede tekrar eden" value={data.counts.duplicate} tone="bg-[#FFF6E5] text-[#8a6d1f]" testid="bulk-count-dup" />
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5">
        <p className="text-sm font-medium text-[#2A2224]">2. Mesajı düzenleyin</p>
        <p className="text-xs text-[#7C6E70] mt-1"><b>{"{firma}"}</b> firma adıyla, <b>{"{yetkili}"}</b> yetkili adıyla değiştirilir.</p>
        <textarea value={tpl} onChange={(e) => setTpl(e.target.value)} rows={4} data-testid="bulk-sms-template"
          className="mt-3 w-full rounded-xl border border-[#F0DDE0] bg-[#FBF5F0] p-3 text-sm outline-none focus:border-[#C15B6B]" />
        <div className="mt-3 rounded-xl bg-[#F3F7FD] p-3 text-sm text-[#2b4c7e]" data-testid="bulk-sms-sample">
          <p className="text-xs text-[#5a7096] mb-1">Örnek mesaj · {sample.length} karakter · {parts(sample.length)} SMS kredisi</p>
          {sample}
        </div>
        <button onClick={() => setTpl(DEFAULT_TPL)} className="mt-2 text-xs text-[#9E2A3B] hover:underline" data-testid="bulk-sms-reset-tpl">Standart şablona dön</button>
      </div>

      <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="text-sm text-[#5b4d4f]">
          {progress ? `Gönderiliyor... ${fmt(progress.done)} / ${fmt(progress.total)}` : ready.length ? `${fmt(ready.length)} firmaya gönderilmeye hazır · yaklaşık ${fmt(ready.length * parts(sample.length))} kredi` : "Gönderilecek firma yok."}
          {progress && <div className="mt-2 h-2 w-64 max-w-full rounded-full bg-[#FBEEF0] overflow-hidden"><div className="h-full bg-[#9E2A3B] transition-[width]" style={{ width: `${(progress.done / progress.total) * 100}%` }} /></div>}
        </div>
        <button onClick={() => setConfirm(true)} disabled={!ready.length || !!progress || tpl.trim().length < 10} data-testid="bulk-sms-send-btn"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#9E2A3B] text-white text-sm font-medium hover:bg-[#861f2f] transition-colors disabled:opacity-50">
          {progress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} SMS Gönder
        </button>
      </div>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="sm:max-w-md" data-testid="bulk-sms-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display"><FileSpreadsheet className="w-5 h-5 text-[#9E2A3B]" /> Gönderimi onaylayın</DialogTitle>
            <DialogDescription>{fmt(ready.length)} firmaya SMS gönderilecek. Bu işlem geri alınamaz ve Netgsm kredinizden düşer.</DialogDescription>
          </DialogHeader>
          <p className="rounded-xl bg-[#FBF5F0] p-3 text-sm text-[#5b4d4f]">{sample}</p>
          <button onClick={send} data-testid="bulk-sms-confirm-btn" className="w-full h-11 rounded-xl bg-[#9E2A3B] text-white font-medium hover:bg-[#861f2f] transition-colors">Evet, {fmt(ready.length)} Firmaya Gönder</button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
